/**
 * 客户端核心入口（#89098）
 */

import { resolveRuntimeConfig } from './config.js';
import { anonymousTokenProvider, createCloudFetch } from './http/fetch.js';
import { AuthModule } from './modules/auth/index.js';
import { createDatabaseModule, WorkBuddyDatabaseModule } from './modules/database.js';
import { LlmModule } from './modules/llm.js';
import { createStorageModule } from './modules/storage.js';
import type { CloudRuntimeConfig, WorkBuddyCloudOptions } from './types.js';
import type { WorkBuddyStorageModule } from './modules/storage.js';

/**
 * 一个应用一个实例。**初始化一次**，四个模块共用同一份配置与同一个 fetch。
 *
 * ============================================================================
 * 身份怎么流到 database / storage
 *
 * AuthModule 是**唯一**的身份来源。共享 fetch 拿到的是
 * `() => this.auth.getAccessToken()` 这个函数 —— 每个请求发出前都会调它一次，
 * 于是登录/登出/续期后，database 与 storage 自动带上最新身份，不需要重建
 * client，也不需要调用方手动搬 token。
 *
 * ⚠️ **client 上刻意没有 `sessionToken` 之类的字段。** 会话的唯一真源是
 * AuthModule 背后的 localStorage：在这里缓存一份副本会在「另一个标签页登出/
 * 续期」时变脏，而拿着脏句柄去续期会让会话永久锁死（见
 * modules/auth/session-manager.ts 顶部说明）。supabase-js 与 CloudBase SDK
 * 都是同一个结论 —— 状态归 auth，client 层只持有取值函数。
 * ============================================================================
 */
export class WorkBuddyCloudClient<Database = unknown> {
    /** PostgREST 语义的数据库模块（`/.cloud/database/rest`）。 */
    readonly database: WorkBuddyDatabaseModule<Database>;
    readonly auth: AuthModule;
    /** 对象存储客户端（`/.cloud/storage`）。 */
    readonly storage: WorkBuddyStorageModule;
    readonly llm: LlmModule;

    constructor(
        private readonly config: CloudRuntimeConfig,
        options: Pick<WorkBuddyCloudOptions, 'storage'> = {},
    ) {
        // auth 自己的请求走**不带 session provider** 的出口：`/auth/v1/token`
        // 就是续期端点，让它去调「取 token（必要时先续期）」会死锁。
        this.auth = new AuthModule(config, createCloudFetch(config, anonymousTokenProvider), {
            storage: options.storage,
        });

        // 其余三个模块共用带身份的出口。
        const fetch = createCloudFetch(config, () => this.auth.getAccessToken());
        this.database = createDatabaseModule<Database>(config, fetch);
        this.storage = createStorageModule(config, fetch);
        this.llm = new LlmModule(config, fetch);
    }

    /** 归一化后的数据面基址（无尾斜杠）。 */
    get endpoint(): string {
        return this.config.endpoint;
    }

    /** 归一化后的环境中心 OAuth Relay 基址（无尾斜杠）。 */
    get oauthRelayBaseUrl(): string {
        return this.config.oauthRelayBaseUrl;
    }
}

/**
 * 创建 WorkBuddy Cloud 客户端。
 *
 * `endpoint` 与 `publishableKey` 来自云服务开通后下发的
 * `publicConfig`，都不含 envId 与 provider 凭据（设计文档 §5.2）。
 *
 * ```ts
 * const cloud = createWorkBuddyCloud({
 *     endpoint: 'https://my-app.workbuddy.link',
 *     publishableKey: 'wbpk_wbapp_demo_abc123',
 * });
 *
 * const challenge = await cloud.auth.signInWithOtp({ email });
 * if (!challenge.error) await challenge.data.verify({ token: verificationCode });
 * // 登录后 database / storage 自动带上身份，不需要手动传 token。
 * const { data, error } = await cloud.database.from('todos').select('id, title');
 * ```
 *
 * 传入类型生成器产出的 `Database` 类型可得到强类型查询（对齐 supabase 的
 * `createClient<Database>()`）：`createWorkBuddyCloud<Database>({ ... })`。
 *
 * @throws {WorkBuddyCloudConfigError} 配置缺失或格式不合法时**立即**抛出。
 */
export function createWorkBuddyCloud<Database = unknown>(
    options: WorkBuddyCloudOptions,
): WorkBuddyCloudClient<Database> {
    return new WorkBuddyCloudClient<Database>(resolveRuntimeConfig(options), {
        storage: options.storage,
    });
}
